import { useState, useEffect, useMemo, useRef, FormEvent } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { loadStripe, Appearance, StripeElementsOptions } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  LinkAuthenticationElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { httpsCallable } from 'firebase/functions';
import { ArrowRight, Check, Lock, Tag, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { functions, logAnalyticsEvent } from '@/lib/firebase';
import { oaiqCheckoutStarted, oaiqOrderCreated, setOaiqUserEmail } from '@/lib/oaiq';
import { getMetaBrowserIds, metaInitiateCheckout, metaPurchase, setMetaUserEmail } from '@/lib/metaPixel';
import { consumeCheckoutPrefetch } from '@/lib/checkoutPrefetch';
import { Button } from '@/components/ui/Button';
import { GlassCard } from '@/components/ui/GlassCard';
import { HeroBackground } from '@/components/three';
import { LAUNCH_REGULAR_PRICES, isLaunchWeekActive } from '@/components/ui/LaunchCountdown';
import { PlanQuotaPill } from '@/components/ui/PlanQuotaPill';
import { useIsMobile, isMobileNow, isPhoneDevice } from '@/hooks/useIsMobile';
import { useAuth } from '@/hooks/useAuth';
import {
  PLAN_CONFIGS,
  PlanId,
  isOneTimePlan,
  type BillingInterval,
  annualSavingsPercent,
} from '@shared/types';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

const appearance: Appearance = {
  theme: 'night',
  variables: {
    colorPrimary: '#B08080',
    colorBackground: '#0a0a0a',
    colorText: '#FFFBFE',
    colorTextSecondary: 'rgba(255,251,254,0.65)',
    colorTextPlaceholder: 'rgba(255,251,254,0.3)',
    colorDanger: '#ef4444',
    fontFamily: 'Lexend, system-ui, -apple-system, "SF Pro", sans-serif',
    fontSizeBase: '15px',
    fontWeightNormal: '500',
    fontWeightMedium: '600',
    fontWeightBold: '700',
    borderRadius: '12px',
    spacingUnit: '4px',
  },
  rules: {
    '.Tab, .Block, .Input, .CheckboxInput': {
      backgroundColor: 'rgba(255,255,255,0.03)',
      border: '1px solid rgba(255,255,255,0.08)',
      boxShadow: 'none',
      transition: 'border-color 200ms ease, box-shadow 200ms ease, background-color 200ms ease, transform 200ms ease',
    },
    '.Tab:hover, .Block:hover': {
      backgroundColor: 'rgba(176,128,128,0.06)',
      borderColor: 'rgba(176,128,128,0.3)',
      transform: 'translateY(-1px)',
    },
    '.Tab--selected, .Tab--selected:hover': {
      borderColor: '#B08080',
      boxShadow: '0 0 0 1px #B08080, 0 0 24px rgba(176,128,128,0.3)',
      backgroundColor: 'rgba(176,128,128,0.1)',
    },
    '.TabLabel': {
      color: 'rgba(255,251,254,0.9)',
      fontWeight: '600',
      fontSize: '11.5px',
      lineHeight: '1.15',
      letterSpacing: '0',
    },
    '.TabLabel--selected': {
      color: '#FFFBFE',
      fontWeight: '700',
    },
    '.TabIcon': {
      fill: 'rgba(255,251,254,0.9)',
    },
    '.TabIcon--selected': {
      fill: '#FFFBFE',
    },
    '.Input': {
      color: '#FFFBFE',
      fontWeight: '600',
      padding: '14px',
      fontSize: '15px',
    },
    '.Input::placeholder': {
      color: 'rgba(255,251,254,0.3)',
      fontWeight: '500',
    },
    '.Input:focus': {
      borderColor: '#B08080',
      boxShadow: '0 0 0 3px rgba(176,128,128,0.18), 0 0 24px rgba(176,128,128,0.15)',
      backgroundColor: 'rgba(176,128,128,0.04)',
    },
    '.Label': {
      color: 'rgba(255,251,254,0.7)',
      fontWeight: '600',
      fontSize: '12px',
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
    },
    '.Error': {
      color: '#ef4444',
      fontSize: '13px',
      fontWeight: '500',
    },
    '.RedirectText': {
      color: 'rgba(255,251,254,0.65)',
    },
    '.AccordionItem': {
      backgroundColor: 'rgba(255,255,255,0.03)',
      border: '1px solid rgba(255,255,255,0.08)',
    },
    '.PickerItem': {
      backgroundColor: 'rgba(255,255,255,0.03)',
      color: '#FFFBFE',
      fontWeight: '500',
    },
    '.PickerItem--selected': {
      backgroundColor: 'rgba(176,128,128,0.1)',
      borderColor: '#B08080',
    },
    '.MenuIcon': {
      fill: 'rgba(255,251,254,0.7)',
    },
  },
};

type Stage = 'loading' | 'ready' | 'processing' | 'success' | 'error';

type AppliedDiscount = {
  code: string;
  percentOff: number | null;
  amountOff: number | null;
  duration: 'forever' | 'once' | 'repeating';
  durationInMonths: number | null;
};

function formatDollars(cents: number): string {
  const dollars = cents / 100;
  return dollars % 1 === 0 ? `${dollars}` : dollars.toFixed(2);
}

function describeDiscount(d: AppliedDiscount, originalDollars: number): string {
  const pctLabel = d.percentOff
    ? `${d.percentOff}% off`
    : d.amountOff
    ? `$${(d.amountOff / 100).toFixed(0)} off`
    : 'discount applied';
  if (d.duration === 'forever') return `${pctLabel} forever`;
  if (d.duration === 'once') return `${pctLabel} first month, then $${originalDollars}/mo`;
  if (d.duration === 'repeating' && d.durationInMonths) {
    return `${pctLabel} for ${d.durationInMonths} months, then $${originalDollars}/mo`;
  }
  return pctLabel;
}

// 'agency' is gone: retired, and the server has no getPriceId() branch for it, so
// a stale ?plan=agency link would 500 at intent creation. Dropping it here makes it
// fall back to the default plan instead of dead-ending the buyer.
// 'agency' is here as of 2026-07-24: the restored /pricing grid lists it and
// navigates to /checkout?plan=agency. Without it the guard below silently fell
// back to 'cashflow', so a buyer who picked the $1,299 tier landed on a $149
// checkout page. 'free' stays out - it is a display label, never a checkout target.
// 'lifetime_offer' is here so a phone that lands on /checkout?plan=lifetime_offer
// (a bookmark, a back-button return) resolves to the $59 plan it asked for
// instead of silently falling back to the default and quoting $149. The desktop
// block below still refuses it.
const VALID_PLANS: PlanId[] = ['monthly', 'starter', 'cashflow', 'scale', 'agency', 'lifetime', 'lifetime_offer'];

export function CheckoutPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  // A signed-in buyer can reach this page now: the desktop upgrade path routes
  // here instead of Stripe's hosted page. Everything about the payment itself is
  // identical - only what happens AFTER it differs, because there is no account
  // left to create.
  const { user } = useAuth();
  const [linking, setLinking] = useState(false);

  const planParam = searchParams.get('plan') as PlanId | null;
  const planId: PlanId = planParam && VALID_PLANS.includes(planParam) ? planParam : 'cashflow';
  // ?billing=annual. Lifetime is one-time and can never be annual, so it is forced
  // back to 'month' rather than trusted from the URL.
  //
  // The `annualPrice` guard was added 2026-07-24 with the legacy funnel revert,
  // which stripped annualPrice from every plan. Without it, a hand-typed
  // ?billing=annual rendered "$0/year" (annualPrice undefined -> 0) while
  // getPriceId(plan,'year') still resolved the live $828/$2,088/$5,028 annual
  // price objects, which were never archived. That is a page that quotes one
  // number and charges another - the exact shape that generates disputes.
  const annualOffered = !!PLAN_CONFIGS[planId]?.annualPrice;
  const billingInterval: BillingInterval =
    searchParams.get('billing') === 'annual' && !isOneTimePlan(planId) && annualOffered
      ? 'year'
      : 'month';
  const isAnnual = billingInterval === 'year';
  const plan = PLAN_CONFIGS[planId];
  // Lifetime is a PaymentIntent, not a Subscription. Everything that differs
  // downstream — the callable we hit, the id we hand to /register, whether a
  // promo code can even apply — keys off this one flag.
  const isOneTime = isOneTimePlan(planId);

  // Stripe redirects back here after off-site auth (Cash App Pay, Klarna,
  // 3DS cards). When that happens the URL carries redirect_status + the
  // pre-stamped cid/sid/email we put in return_url, and we skip straight
  // to the success animation instead of creating a fresh subscription.
  const redirectStatus = searchParams.get('redirect_status');
  const returnedCid = searchParams.get('cid');
  const returnedSid = searchParams.get('sid');
  const returnedPid = searchParams.get('pid');
  const returnedEmail = searchParams.get('email');
  const isRedirectReturn =
    !!redirectStatus && !!returnedCid && (!!returnedSid || !!returnedPid);

  // Lifetime is a mobile-only SKU, and this route was the hole in that.
  // VALID_PLANS accepts 'lifetime', nothing downstream gates on device, and the
  // desktop funnel never links here with it — so /checkout?plan=lifetime was a
  // fully working $99 desktop purchase for anyone who typed, pasted, or was sent
  // the URL, badge and all.
  //
  // Two things this must NOT break:
  //   - a real phone. isMobileNow() is synchronous, so it can't read false on
  //     first paint the way useIsMobile() does and bounce a buyer.
  //   - a purchase in flight. Stripe sends off-site auth (3DS, Cash App Pay,
  //     Klarna) back to this same URL, so a redirect return is always let
  //     through — that user has already paid and is owed their success screen.
  // isOneTimePlan, not a literal: 'lifetime_offer' is the same SKU at $59 and
  // must be blocked on desktop for the same reason. Checking planParam === was
  // how the first one leaked.
  const blockDesktopLifetime = isOneTimePlan(planParam) && !isMobileNow() && !isRedirectReturn;
  useEffect(() => {
    if (blockDesktopLifetime) navigate('/', { replace: true });
  }, [blockDesktopLifetime, navigate]);

  // INVERTED 2026-07-24. Desktop is embedded, mobile is hosted — the opposite of
  // what this page did between 2026-07-12 and the legacy revert.
  //
  // Desktop renders the Payment Element here, which is what the legacy funnel did
  // and what the thanos snap on HomePage is actually transitioning INTO. The old
  // arrangement was the worst of both: HomePage played a 1.3s snap, this page
  // mounted and drew its skeleton, and only then did it bounce to hosted.
  //
  // Mobile now redirects straight from HomePage and never reaches this page in the
  // normal flow. This branch survives as the fallback for a phone that lands on
  // /checkout directly — a bookmark, a shared link, a back-button return — so it
  // still gets hosted rather than an embedded page that was not designed for it.
  //
  // Still NOT redirected: one-time (lifetime). Its embedded path is the
  // payment-verified one, and lifetime is no longer sold anyway; the branch exists
  // for the two customers who already bought it.
  //
  // isPhoneDevice(), not isMobileNow(). isMobileNow() also returns true for any
  // viewport under 768px, so a desktop browser in a narrow window — or with
  // devtools docked, which is how this was found — got the thanos snap, then this
  // page's skeleton, and then a bounce to Stripe's hosted page. Desktop is
  // embedded, always; only an actual handheld is allowed down this branch.
  const useHostedCheckout = isPhoneDevice() && !isOneTimePlan(planId) && !isRedirectReturn;
  const [hostedError, setHostedError] = useState<string | null>(null);

  useEffect(() => {
    if (!useHostedCheckout || blockDesktopLifetime) return;
    let cancelled = false;
    (async () => {
      try {
        logAnalyticsEvent('checkout_hosted_redirect', { plan_id: planId, value: plan.price });
        // Fire before the redirect: once window.location changes this page is
        // gone, and Stripe's hosted page carries no pixel of ours.
        oaiqCheckoutStarted(planId, plan.name, Math.round(plan.price * 100));
        metaInitiateCheckout(planId, plan.name, Math.round(plan.price * 100));
        const referral = (window as unknown as { Rewardful?: { referral?: string } }).Rewardful
          ?.referral;
        const createHosted = httpsCallable<
          {
            planId: PlanId;
            billingInterval: BillingInterval;
            referral?: string;
            metaFbp?: string;
            metaFbc?: string;
          },
          // The callable returns `sessionUrl`, not `url`. Reading the wrong key here
          // fails silently into "No checkout URL returned" and kills every desktop
          // checkout — which is exactly what it did the first time.
          { sessionUrl: string }
        >(functions, 'createGuestCheckout');
        // No `withTrial`. No plan carries a trial any more, and the server defaults
        // the flag to false — so the client cannot ask for one even by mistake.
        const { data } = await createHosted({
          planId,
          billingInterval,
          ...(referral ? { referral } : {}),
          ...getMetaBrowserIds(),
        });
        if (cancelled) return;
        if (!data?.sessionUrl) throw new Error('No checkout URL returned');
        window.location.href = data.sessionUrl;
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'Could not start checkout';
        setHostedError(msg);
        setStage('error');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useHostedCheckout, blockDesktopLifetime, planId, billingInterval]);

  const [stage, setStage] = useState<Stage>(
    isRedirectReturn && redirectStatus === 'succeeded' ? 'success' : 'loading',
  );
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(
    isRedirectReturn ? returnedCid : null,
  );
  const [subscriptionId, setSubscriptionId] = useState<string | null>(
    isRedirectReturn ? returnedSid : null,
  );
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(
    isRedirectReturn ? returnedPid : null,
  );
  // Which Stripe object backs clientSecret. A trialing subscription has no invoice
  // to pay, so the server hands back a SetupIntent — and a SetupIntent must be
  // confirmed with stripe.confirmSetup(), not stripe.confirmPayment(). Getting this
  // wrong doesn't throw; it just never charges anyone.
  const [intentMode, setIntentMode] = useState<'payment' | 'setup'>('payment');
  const [confirmedEmail, setConfirmedEmail] = useState(
    isRedirectReturn && returnedEmail ? returnedEmail : '',
  );
  // Lifted up here (instead of inside UnifiedPaymentForm) because applying a
  // promo code recreates the Stripe subscription and swaps clientSecret, which
  // remounts Elements and would otherwise wipe the applied-discount state.
  const [appliedDiscount, setAppliedDiscount] = useState<AppliedDiscount | null>(null);
  const [currentAmountCents, setCurrentAmountCents] = useState(Math.round(plan.price * 100));

  useEffect(() => {
    // Bail before any intent is created. Without this the redirect above still
    // races the async creator below, and a desktop visitor to
    // /checkout?plan=lifetime would mint a live $99 PaymentIntent on their way
    // out the door. Same for the hosted redirect: no point minting an embedded
    // intent for a page the user is already leaving.
    if (blockDesktopLifetime || useHostedCheckout) return;

    logAnalyticsEvent('checkout_page_view', { plan_id: planId, value: plan.price });
    if (!isRedirectReturn) {
      oaiqCheckoutStarted(planId, plan.name, Math.round(plan.price * 100));
      metaInitiateCheckout(planId, plan.name, Math.round(plan.price * 100));
    }

    // Post-redirect: if Stripe sent us back with a success status we already
    // have everything we need to navigate to /register. Skip intent creation
    // entirely.
    if (isRedirectReturn) {
      if (redirectStatus === 'succeeded') {
        logAnalyticsEvent('checkout_payment_succeeded', { value: plan.price, via: 'redirect' });
        // Same event_id RegisterPage will use a moment later, so the buyer
        // counts once no matter how many of our pages watch them pay.
        if (returnedEmail) void setOaiqUserEmail(returnedEmail);
        if (returnedEmail) void setMetaUserEmail(returnedEmail);
        const redirectEventId = returnedSid || returnedPid;
        if (redirectEventId) {
          oaiqOrderCreated({
            eventId: redirectEventId,
            planId,
            planName: plan.name,
            amountCents: currentAmountCents,
          });
          metaPurchase({
            eventId: redirectEventId,
            planId,
            planName: plan.name,
            amountCents: currentAmountCents,
          });
        }
        // Stage is already 'success' from the initial state; SuccessReveal's
        // onContinue effect will handle the navigation to /register.
      } else if (redirectStatus === 'failed') {
        toast.error('Payment failed. Please try again.');
        setStage('error');
      } else {
        // 'processing' or other — let user retry from a fresh intent.
        setStage('loading');
      }
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        // Fast path: HomePage / PricingPage CTAs prefetch the subscription in
        // parallel with the thanos snap. If the call resolved during the
        // animation we read it from sessionStorage; if it's still in flight
        // we await the same promise instead of firing a duplicate.
        const prefetched = consumeCheckoutPrefetch(planId, billingInterval);
        if (prefetched) {
          logAnalyticsEvent('checkout_intent_prefetched', { plan_id: planId, value: plan.price });
          const data = prefetched instanceof Promise ? await prefetched : prefetched;
          if (cancelled) return;
          setClientSecret(data.clientSecret);
          setCustomerId(data.customerId);
          setSubscriptionId(data.subscriptionId ?? null);
          setPaymentIntentId(data.paymentIntentId ?? null);
          setIntentMode(data.mode ?? 'payment');
          setStage('ready');
          return;
        }

        const create = httpsCallable<
          { planId: PlanId; billingInterval?: BillingInterval; referral?: string; metaFbp?: string; metaFbc?: string },
          {
            clientSecret: string;
            customerId: string;
            subscriptionId?: string;
            paymentIntentId?: string;
            mode?: 'payment' | 'setup';
          }
        >(functions, isOneTime ? 'createGuestLifetimePayment' : 'createGuestSubscription');
        logAnalyticsEvent('checkout_intent_created', { plan_id: planId, value: plan.price });
        // Rewardful sets a cookie when the visitor lands via ?via=<affiliate>.
        // Forward the referral UUID so the cloud function can attach it as
        // metadata.referral on the Stripe Customer for attribution.
        const referral = (window as unknown as { Rewardful?: { referral?: string } }).Rewardful?.referral;
        const { data } = await create({
          planId,
          // The lifetime callable's schema is strict and rejects unknown keys.
          ...(isOneTime ? {} : { billingInterval, ...getMetaBrowserIds() }),
          ...(referral ? { referral } : {}),
        });
        if (cancelled) return;
        setClientSecret(data.clientSecret);
        setCustomerId(data.customerId);
        setSubscriptionId(data.subscriptionId ?? null);
        setPaymentIntentId(data.paymentIntentId ?? null);
        setIntentMode(data.mode ?? 'payment');
        setStage('ready');
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'Failed to start checkout';
        toast.error(msg);
        setStage('error');
      }
    })();
    return () => { cancelled = true; };
  }, [planId, plan.price, isRedirectReturn, redirectStatus]);

  const elementsOptions: StripeElementsOptions | null = useMemo(() => {
    if (!clientSecret) return null;
    return {
      clientSecret,
      appearance,
      loader: 'auto',
      // Load Lexend INSIDE Stripe's iframe so the .Input fontFamily resolves.
      // Without this, Stripe falls back to system fonts because Google Fonts
      // loaded by the parent page can't be accessed by the iframe.
      fonts: [
        {
          cssSrc:
            'https://fonts.googleapis.com/css2?family=Lexend:wght@400;500;600;700&display=swap',
        },
      ],
    };
  }, [clientSecret]);

  return (
    <HeroBackground>
      <div className="min-h-screen flex items-start justify-center px-4 pt-28 pb-16 relative overflow-hidden">
        {/* Rose ambient lift so the form area reads above the cosmic black,
            matching the warm tint of HomePage / PricingPage. */}
        <div
          className="fixed inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse 900px 700px at 50% 38%, rgba(176,128,128,0.09), transparent 70%)',
          }}
        />
        <nav
          className="fixed top-0 left-0 right-0 z-50"
          style={{
            background: 'rgba(0,0,0,0.8)',
            backdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(255,255,255,0.05)',
          }}
        >
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg border-2 border-white bg-black flex items-center justify-center overflow-hidden">
                <img src="/wolfromulus.png" alt="Romulus" className="w-8 h-8 object-contain" />
              </div>
              <span className="text-2xl font-bold text-white font-roman tracking-wider">ROMVLVS</span>
            </Link>
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <Lock className="w-3 h-3" />
              Secured by Stripe
            </div>
          </div>
        </nav>

        <AnimatePresence mode="wait">
          {stage === 'success' ? (
            <SuccessReveal
              key="success"
              email={confirmedEmail}
              onContinue={async () => {
                // ALREADY SIGNED IN: attach the purchase to the existing uid and
                // go to the dashboard. Sending them to /register would ask a
                // logged-in user to create a second account for a subscription
                // they just paid for. linkGuestSubscription / linkGuestLifetime
                // both authenticate off the caller's token, so no new callable
                // was needed - the same ones /register uses do the job here.
                if (user) {
                  if (linking) return;
                  setLinking(true);
                  try {
                    if (isOneTime) {
                      await httpsCallable(functions, 'linkGuestLifetime')({
                        customerId,
                        paymentIntentId,
                      });
                    } else {
                      await httpsCallable(functions, 'linkGuestSubscription')({
                        customerId,
                        subscriptionId,
                      });
                    }
                    navigate('/dashboard', { replace: true });
                  } catch (err) {
                    setLinking(false);
                    // The money is taken and the ids are in the URL-less state
                    // here, so /register's recoverPurchase flow is the fallback
                    // rather than a dead end.
                    toast.error(
                      err instanceof Error ? err.message : 'Payment succeeded but linking failed',
                    );
                    const reff = isOneTime ? `pid=${paymentIntentId}` : `sid=${subscriptionId}`;
                    navigate(
                      `/register?cid=${customerId}&${reff}&plan=${planId}&email=${encodeURIComponent(confirmedEmail)}`,
                    );
                  }
                  return;
                }
                // Lifetime carries a PaymentIntent id (pid); subscriptions carry
                // a Subscription id (sid). RegisterPage picks its link-callable
                // based on which one is present.
                const ref = isOneTime
                  ? `pid=${paymentIntentId}`
                  : `sid=${subscriptionId}`;
                navigate(
                  `/register?cid=${customerId}&${ref}&plan=${planId}&email=${encodeURIComponent(confirmedEmail)}`,
                );
              }}
            />
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 24, filter: 'blur(8px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              exit={{ opacity: 0, y: -16, filter: 'blur(8px)', scale: 0.98 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="w-full max-w-lg z-10"
            >
              <CheckoutHeader plan={plan} planId={planId} isMobile={isMobile} isAnnual={isAnnual} />

              <GlassCard className="p-7 backdrop-blur-2xl bg-black/50 border-white/10">
                <AnimatePresence mode="wait">
                  {stage === 'loading' && <FullFormSkeleton key="loading-full" />}

                  {stage === 'ready' && elementsOptions && (
                    <motion.div
                      key="ready"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Elements
                        key={clientSecret}
                        stripe={stripePromise}
                        options={elementsOptions}
                      >
                        <UnifiedPaymentForm
                          plan={plan}
                          planId={planId}
                          isMobile={isMobile}
                          intentMode={intentMode}
                          isOneTime={isOneTime}
                          customerId={customerId!}
                          subscriptionId={subscriptionId}
                          paymentIntentId={paymentIntentId}
                          appliedDiscount={appliedDiscount}
                          currentAmountCents={currentAmountCents}
                          onApplyDiscount={(amount, discount, newSubId, newCustomerId, newClientSecret) => {
                            setCurrentAmountCents(amount);
                            setAppliedDiscount(discount);
                            setSubscriptionId(newSubId);
                            setCustomerId(newCustomerId);
                            setClientSecret(newClientSecret);
                          }}
                          onRemoveDiscount={(amount, newSubId, newCustomerId, newClientSecret) => {
                            setCurrentAmountCents(amount);
                            setAppliedDiscount(null);
                            setSubscriptionId(newSubId);
                            setCustomerId(newCustomerId);
                            setClientSecret(newClientSecret);
                          }}
                          onProcessing={() => setStage('processing')}
                          onSuccess={(email) => {
                            setConfirmedEmail(email);
                            setStage('success');
                            void setOaiqUserEmail(email);
                            void setMetaUserEmail(email);
                            // currentAmountCents, not plan.price: a promo code
                            // rewrites what was actually charged.
                            const eventId = subscriptionId || paymentIntentId;
                            if (eventId) {
                              oaiqOrderCreated({
                                eventId,
                                planId,
                                planName: plan.name,
                                amountCents: currentAmountCents,
                              });
                              metaPurchase({
                                eventId,
                                planId,
                                planName: plan.name,
                                amountCents: currentAmountCents,
                              });
                            }
                          }}
                          onError={() => setStage('ready')}
                        />
                      </Elements>
                    </motion.div>
                  )}

                  {stage === 'processing' && <ProcessingState key="processing" />}

                  {stage === 'error' && (
                    <ErrorState
                      key="error"
                      message={hostedError ?? undefined}
                      onRetry={() => window.location.reload()}
                    />
                  )}
                </AnimatePresence>
              </GlassCard>

            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </HeroBackground>
  );
}

function CheckoutHeader({
  plan,
  planId,
  isMobile,
  isAnnual,
}: {
  plan: typeof PLAN_CONFIGS[PlanId];
  planId: PlanId;
  isMobile: boolean;
  isAnnual: boolean;
}) {
  const isOneTime = isOneTimePlan(planId);

  // Suppress the whole discount apparatus on mobile.
  //
  // Cold mobile traffic has no reference price for Romulus, so an anchor it
  // can't verify costs credibility instead of buying it.
  //
  // The launch-week check is what every other price surface already does
  // (SettingsPage, QuotaLimitModal, the funnel's plan picker). Without it this
  // header kept announcing "Founder pricing · 63% off" against a struck-through
  // $399 that stopped being the price on 2026-04-24 — while the plan picker one
  // click earlier showed a clean $149. The contradiction landed at the moment of
  // payment.
  //
  // Lifetime never gets a strikethrough on any device: it has no LAUNCH_REGULAR
  // anchor, and inventing one for a brand-new plan is exactly the move that
  // makes a $99 offer look fake.
  const regularPrice =
    isLaunchWeekActive() && !isMobile && !isOneTime && planId !== 'free'
      ? LAUNCH_REGULAR_PRICES[planId as 'starter' | 'cashflow' | 'scale' | 'agency']
      : null;
  const showStrikethrough = !!regularPrice && regularPrice !== plan.price;

  // No plan carries a trial (see shared/types.ts), so there is no "free until" state
  // left to render here. Annual must never print a monthly figure as if that were the
  // charge: the card is about to be hit for the full year, and the number on the page
  // has to be the number on the statement.
  const annualTotal = plan.annualPrice ?? 0;
  const annualPerMonth = annualTotal ? Math.round(annualTotal / 12) : 0;
  const annualSaving = annualSavingsPercent(planId);

  // "Founder spot held" is gone (2026-07-12). No spot was ever held: there was no
  // cap, no counter, and no reservation behind it — it rendered for every visitor,
  // forever, and would have kept doing so. Invented scarcity is one of the
  // practices the FTC enumerated in its Notice of Penalty Offenses Concerning
  // Money-Making Opportunities, and on a payment page in this category it is the
  // loudest scam signal available.
  //
  // The badge that replaced it announced the free trial. The trial is gone too
  // (2026-07-13), so there is no badge: nothing true and non-obvious is left to put
  // in one, and an empty pill full of invented urgency is how we got here the first
  // time. The price and charge date below carry the whole message.

  return (
    <div className="text-center mb-7">
      <motion.h1
        className="text-3xl md:text-4xl font-bold text-white mb-2"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
        style={{ textShadow: '0 0 60px rgba(91,58,58,0.5)' }}
      >
        {isOneTime ? (
          <>Get <span className="text-primary-300">{plan.name}</span> access</>
        ) : (
          <>Claim your <span className="text-primary-300">{plan.name}</span> plan</>
        )}
      </motion.h1>

      <motion.div
        className="flex items-center justify-center gap-2.5 text-sm"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: 0.15 }}
      >
        {showStrikethrough && (
          <span className="text-gray-500 line-through tabular-nums text-base">
            ${regularPrice}
          </span>
        )}
        <span className="text-white font-bold text-base">
          {isAnnual
            ? `$${annualTotal.toLocaleString()}/year`
            : `$${plan.price}${isOneTime ? ' once' : '/mo'}`}
        </span>
        <span className="text-gray-500">·</span>
        <span className="text-gray-400">
          {isAnnual ? `$${annualPerMonth}/mo, billed yearly` : '100% of every sale is yours'}
        </span>
      </motion.div>

      {/* Say the amount and the date out loud, before the card form rather than after
          it. Burying when and how much you'll charge is the precise thing the FTC
          fined Amazon $2.5B over in Sept 2025 (billing info taken before material
          terms were disclosed). That reasoning did not leave with the trial — it just
          has a simpler fact to state now: we charge today. */}
      {isAnnual && (
        <motion.p
          className="mt-3 text-[13px] text-gray-400"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.4 }}
        >
          We charge <span className="text-gray-200 font-medium">${annualTotal.toLocaleString()}</span> today for
          12 months. That is {annualSaving}% less than paying monthly.
        </motion.p>
      )}

      {!isAnnual && !isOneTime && (
        <motion.p
          className="mt-3 text-[13px] text-gray-400"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.4 }}
        >
          We charge <span className="text-gray-200 font-medium">${plan.price}</span> today, then
          the same on this date each month. Cancel any time and you are not billed again.
        </motion.p>
      )}

      <motion.div
        className="mt-5 flex justify-center"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25, duration: 0.4 }}
      >
        <PlanQuotaPill plan={plan} />
      </motion.div>

      <motion.div
        className="mt-5 flex justify-center"
        initial={{ opacity: 0, scale: 0.95, y: 6 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ delay: 0.35, duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      >
        <div
          className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/[0.04] border border-white/15"
          style={{ boxShadow: '0 0 30px rgba(255,255,255,0.04)' }}
        >
          <Lock className="w-4 h-4 text-white" />
          <span className="text-sm text-gray-200 font-medium leading-none">Powered by</span>
          <svg viewBox="0 0 60 25" className="h-4 fill-white" aria-label="Stripe">
            <path d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM39.96 9.1a2.4 2.4 0 0 0-1.9.86l.02 6.1a2.33 2.33 0 0 0 1.88.84c1.48 0 2.47-1.6 2.47-3.91 0-2.25-1-3.89-2.47-3.89zM28.92 5.57h4.13v14.44h-4.13V5.57zm0-4.7L33.05 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79h-4.12V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.13v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 0 1-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.58-.24 1.58-1C6.99 13.93 0 14.67 0 9.6 0 6.67 2.18 4.93 5.5 4.93c1.59 0 3.18.24 4.77.87v3.85c-1.46-.78-3.3-1.22-4.78-1.22-.87 0-1.4.25-1.4.9 0 1.73 7.04.91 7.04 6.31z" />
          </svg>
          <span className="w-px h-4 bg-white/20" />
          <span className="text-sm text-gray-200 font-medium leading-none">256-bit encrypted</span>
        </div>
      </motion.div>
    </div>
  );
}

function FullFormSkeleton() {
  return (
    <motion.div
      key="loading-full"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-4"
    >
      <SkeletonField height="h-14" delay={0} />
      <SkeletonField height="h-12" delay={0.05} />
      <div className="grid grid-cols-4 gap-2.5 pt-2">
        {[0, 1, 2, 3].map((i) => (
          <SkeletonField key={i} height="h-16" delay={0.1 + i * 0.05} />
        ))}
      </div>
      <SkeletonField height="h-12" delay={0.3} />
      <div className="grid grid-cols-2 gap-3">
        <SkeletonField height="h-12" delay={0.35} />
        <SkeletonField height="h-12" delay={0.4} />
      </div>
      <SkeletonField height="h-16" delay={0.5} />
      <p className="text-center text-[11px] text-gray-500 pt-2">
        Opening secure checkout…
      </p>
    </motion.div>
  );
}

function UnifiedPaymentForm({
  plan,
  planId,
  isMobile,
  isOneTime,
  intentMode,
  customerId,
  subscriptionId,
  paymentIntentId,
  appliedDiscount,
  currentAmountCents,
  onApplyDiscount,
  onRemoveDiscount,
  onProcessing,
  onSuccess,
  onError,
}: {
  plan: typeof PLAN_CONFIGS[PlanId];
  planId: PlanId;
  isMobile: boolean;
  isOneTime: boolean;
  intentMode: 'payment' | 'setup';
  customerId: string;
  subscriptionId: string | null;
  paymentIntentId: string | null;
  appliedDiscount: AppliedDiscount | null;
  currentAmountCents: number;
  onApplyDiscount: (
    amount: number,
    discount: AppliedDiscount,
    newSubId: string,
    newCustomerId: string,
    newClientSecret: string,
  ) => void;
  onRemoveDiscount: (
    amount: number,
    newSubId: string,
    newCustomerId: string,
    newClientSecret: string,
  ) => void;
  onProcessing: () => void;
  onSuccess: (email: string) => void;
  onError: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [paymentReady, setPaymentReady] = useState(false);
  const [linkReady, setLinkReady] = useState(false);
  const [email, setEmail] = useState('');

  // Same suppression as CheckoutHeader: expired launch week shows no anchor, no
  // invented anchor on mobile, and never one for a one-time plan. A discount the
  // user actually applied still shows, because that one is real.
  const regularPrice =
    isLaunchWeekActive() && !isMobile && !isOneTime && planId !== 'free'
      ? LAUNCH_REGULAR_PRICES[planId as 'starter' | 'cashflow' | 'scale' | 'agency']
      : null;
  const showStrikethrough = !!regularPrice && regularPrice !== plan.price;
  const displayPrice = appliedDiscount ? formatDollars(currentAmountCents) : `${plan.price}`;
  const strikethroughValue = appliedDiscount
    ? plan.price
    : showStrikethrough
    ? regularPrice
    : null;

  // The promo-code flow works by cancelling and recreating a Stripe
  // *Subscription*. A lifetime purchase is a bare PaymentIntent, so there is
  // nothing for it to act on — hide the field rather than ship a button that
  // throws.
  const canUsePromoCode = !isOneTime && !!subscriptionId;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      toast.error('Please enter a valid email');
      return;
    }

    setSubmitting(true);
    onProcessing();
    logAnalyticsEvent('checkout_payment_submitted', { value: plan.price });

    // Embed everything we'll need for the post-redirect mount in return_url.
    // Required for Cash App Pay / Klarna / 3DS card flows where Stripe
    // navigates the user away and back. Card-only flows with redirect:'if_required'
    // never actually use this URL but Stripe requires the field to be set.
    const returnParams = new URLSearchParams({
      cid: customerId,
      ...(isOneTime
        ? { pid: paymentIntentId ?? '' }
        : { sid: subscriptionId ?? '' }),
      plan: planId,
      email,
    });
    const returnUrl = `${window.location.origin}/checkout?${returnParams.toString()}`;

    // A trial collects the card without charging it, so Stripe issued a SetupIntent
    // — and confirmPayment() cannot confirm one. It fails quietly rather than
    // loudly, which is how a broken money path stays broken: the buyer sees a
    // spinner, no card is ever authorized, and Stripe cancels the unconfirmed
    // subscription at trial end. Branch on what the server actually gave us.
    const { error } = intentMode === 'setup'
      ? await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: returnUrl,
            payment_method_data: {
              billing_details: { email },
            },
          },
          redirect: 'if_required',
        })
      : await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: returnUrl,
            receipt_email: email,
            payment_method_data: {
              billing_details: { email },
            },
          },
          redirect: 'if_required',
        });

    if (error) {
      logAnalyticsEvent('checkout_payment_failed', { error: error.code, value: plan.price });
      toast.error(error.message || 'Payment failed');
      setSubmitting(false);
      onError();
      return;
    }

    logAnalyticsEvent(
      intentMode === 'setup' ? 'checkout_trial_started' : 'checkout_payment_succeeded',
      { value: plan.price },
    );
    onSuccess(email);
  };

  const ready = paymentReady && linkReady;
  const completeness = (email ? 0.5 : 0) + (paymentReady ? 0.5 : 0);

  return (
    <form onSubmit={handleSubmit} className="space-y-5 relative">
      <div className="relative min-h-[420px]">
        <AnimatePresence>
          {!ready && (
            <motion.div
              key="form-skeleton"
              initial={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
              className="absolute inset-0 z-10 space-y-4 pointer-events-none"
            >
              <SkeletonField height="h-14" delay={0} />
              <SkeletonField height="h-12" delay={0.05} />
              <div className="grid grid-cols-4 gap-2.5 pt-2">
                {[0, 1, 2, 3].map((i) => (
                  <SkeletonField key={i} height="h-16" delay={0.1 + i * 0.05} />
                ))}
              </div>
              <SkeletonField height="h-12" delay={0.3} />
              <div className="grid grid-cols-2 gap-3">
                <SkeletonField height="h-12" delay={0.35} />
                <SkeletonField height="h-12" delay={0.4} />
              </div>
              <p className="text-center text-[11px] text-gray-500 pt-2">
                Opening secure checkout…
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div
          initial={{ opacity: 0 }}
          animate={ready ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          className="space-y-5"
        >
          <motion.div
            initial={{ y: 8 }}
            animate={ready ? { y: 0 } : { y: 8 }}
            transition={{ duration: 0.4, delay: 0.05 }}
          >
            <LinkAuthenticationElement
              options={{ defaultValues: { email } }}
              onChange={(e) => setEmail(e.value.email)}
              onReady={() => setLinkReady(true)}
            />
          </motion.div>

          <motion.div
            className="min-h-[260px]"
            initial={{ y: 12 }}
            animate={ready ? { y: 0 } : { y: 12 }}
            transition={{ duration: 0.4, delay: 0.15 }}
          >
            <PaymentElement
              options={{
                layout: { type: 'tabs', defaultCollapsed: false },
              }}
              onReady={() => setPaymentReady(true)}
            />
          </motion.div>
        </motion.div>
      </div>

      <AnimatePresence mode="wait">
        {ready && canUsePromoCode && (
          <motion.div
            key="coupon-field"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <CouponField
              subscriptionId={subscriptionId!}
              originalDollars={plan.price}
              applied={appliedDiscount}
              onApply={onApplyDiscount}
              onRemove={onRemoveDiscount}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        className="relative rounded-xl"
        animate={
          ready && email
            ? {
                boxShadow: [
                  `0 0 0px rgba(176,128,128,0)`,
                  `0 0 ${20 + completeness * 24}px rgba(176,128,128,${0.3 + completeness * 0.3})`,
                  `0 0 0px rgba(176,128,128,0)`,
                ],
              }
            : {}
        }
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      >
        <Button
          type="submit"
          size="lg"
          loading={submitting}
          disabled={!ready || !stripe || !elements}
          className="w-full h-16 text-lg font-bold group relative overflow-hidden"
        >
          <span className="relative z-10 flex items-center justify-center gap-2.5">
            {/* The button states the amount it is about to take, because it takes it
                today. It used to read "Start my 7-day trial" next to a form that
                charged $0 — the trial is gone, so the price goes back on the button. */}
            <span>{isOneTime ? 'Get lifetime access ·' : 'Subscribe ·'}</span>
            {strikethroughValue !== null && (
              <span className="text-white/50 line-through tabular-nums text-base font-medium">
                ${strikethroughValue}
              </span>
            )}
            <span className="tabular-nums">
              ${displayPrice}
              {isOneTime ? '' : '/mo'}
            </span>
            <ArrowRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
          </span>
        </Button>
      </motion.div>

      <motion.div
        className="flex items-center justify-center gap-4 text-[11px] text-gray-500"
        initial={{ opacity: 0 }}
        animate={ready ? { opacity: 1 } : { opacity: 0 }}
        transition={{ delay: 0.4 }}
      >
        <span className="flex items-center gap-1.5">
          <Lock className="w-3 h-3" />
          256-bit encrypted
        </span>
        <span className="w-px h-3 bg-gray-700" />
        {/* No refund promise on any surface: payments are non-refundable (ToS 7.3). */}
        <span>{isOneTime ? 'One-time payment' : 'Cancel anytime'}</span>
      </motion.div>
    </form>
  );
}

function CouponField({
  subscriptionId,
  originalDollars,
  applied,
  onApply,
  onRemove,
}: {
  subscriptionId: string;
  originalDollars: number;
  applied: AppliedDiscount | null;
  onApply: (
    amountCents: number,
    discount: AppliedDiscount,
    newSubId: string,
    newCustomerId: string,
    newClientSecret: string,
  ) => void;
  onRemove: (
    amountCents: number,
    newSubId: string,
    newCustomerId: string,
    newClientSecret: string,
  ) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  const handleApply = async () => {
    const trimmed = code.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      const fn = httpsCallable<
        { subscriptionId: string; code: string },
        {
          amount: number;
          currency: string;
          clientSecret: string;
          subscriptionId: string;
          customerId: string;
          discount: AppliedDiscount;
        }
      >(functions, 'applyPromoCode');
      const { data } = await fn({ subscriptionId, code: trimmed });
      // The server cancelled the old incomplete sub and created a fresh one
      // with the discount. Propagate the new clientSecret/subId up so the
      // parent can remount Elements at the new PaymentIntent.
      onApply(data.amount, data.discount, data.subscriptionId, data.customerId, data.clientSecret);
      logAnalyticsEvent('checkout_promo_applied', { code: data.discount.code });
      toast.success(`${data.discount.code} applied`);
      setCode('');
      setExpanded(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not apply code';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const fn = httpsCallable<
        { subscriptionId: string },
        {
          amount: number;
          currency: string;
          clientSecret: string;
          subscriptionId: string;
          customerId: string;
        }
      >(functions, 'removePromoCode');
      const { data } = await fn({ subscriptionId });
      onRemove(data.amount, data.subscriptionId, data.customerId, data.clientSecret);
      logAnalyticsEvent('checkout_promo_removed');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not remove code';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (applied) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl border border-primary-500/40 bg-primary-500/[0.08]"
      >
        <Tag className="w-3.5 h-3.5 text-primary-300 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-[12px] font-semibold text-primary-100 leading-tight">
            {applied.code} applied
          </p>
          <p className="text-[11px] text-primary-300/80 leading-tight mt-0.5 truncate">
            {describeDiscount(applied, originalDollars)}
          </p>
        </div>
        <button
          type="button"
          onClick={handleRemove}
          disabled={busy}
          aria-label="Remove code"
          className="p-1 rounded-md hover:bg-white/10 transition-colors disabled:opacity-40"
        >
          {busy ? (
            <Loader2 className="w-3.5 h-3.5 text-white/70 animate-spin" />
          ) : (
            <X className="w-3.5 h-3.5 text-white/70" />
          )}
        </button>
      </motion.div>
    );
  }

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="text-[12px] font-medium text-primary-300 hover:text-primary-200 inline-flex items-center gap-1.5 transition-colors"
      >
        <Tag className="w-3 h-3" />
        Have a code?
      </button>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-center gap-2"
    >
      <input
        type="text"
        autoFocus
        value={code}
        onChange={(e) => setCode(e.target.value.toUpperCase())}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleApply();
          }
          if (e.key === 'Escape') {
            setExpanded(false);
            setCode('');
          }
        }}
        placeholder="Enter code"
        disabled={busy}
        className="flex-1 h-11 px-3.5 rounded-xl border border-white/10 bg-white/[0.03] text-[13px] font-semibold tracking-wider text-white placeholder:text-white/30 focus:border-primary-500/60 focus:outline-none focus:bg-primary-500/[0.04] transition-colors"
      />
      <button
        type="button"
        onClick={handleApply}
        disabled={busy || !code.trim()}
        className="h-11 px-4 rounded-xl border border-primary-500/40 bg-primary-500/10 text-[12px] font-semibold text-primary-100 hover:bg-primary-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-1.5"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
        Apply
      </button>
      <button
        type="button"
        onClick={() => {
          setExpanded(false);
          setCode('');
        }}
        aria-label="Cancel"
        className="h-11 w-11 rounded-xl border border-white/10 bg-white/[0.02] text-white/60 hover:bg-white/[0.06] transition-colors inline-flex items-center justify-center"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </motion.div>
  );
}

function SkeletonField({ height, delay }: { height: string; delay: number }) {
  return (
    <motion.div
      className={`${height} rounded-xl bg-white/[0.04] border border-white/10 relative overflow-hidden`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay, duration: 0.3 }}
    >
      <motion.div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(90deg, transparent, rgba(176,128,128,0.12), transparent)',
        }}
        animate={{ x: ['-100%', '100%'] }}
        transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut', delay }}
      />
    </motion.div>
  );
}

function ProcessingState() {
  return (
    <motion.div
      key="processing"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="py-12 flex flex-col items-center gap-4"
    >
      <motion.div
        className="w-16 h-16 rounded-full border-2 border-primary-500/30 border-t-primary-400"
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
      />
      <p className="text-gray-300 text-sm">Confirming with your bank…</p>
    </motion.div>
  );
}

function ErrorState({ onRetry, message }: { onRetry: () => void; message?: string }) {
  return (
    <motion.div
      key="error"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="py-8 text-center space-y-4"
    >
      <p className="text-gray-200">{message || 'Something went wrong starting checkout.'}</p>
      <Button onClick={onRetry} variant="outline">Try again</Button>
    </motion.div>
  );
}

function SuccessReveal({
  email,
  onContinue,
}: {
  email: string;
  onContinue: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(onContinue, 1800);
    return () => clearTimeout(t);
  }, [onContinue]);

  return (
    <motion.div
      ref={containerRef}
      key="success"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 1.04, filter: 'blur(8px)' }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="text-center z-10 relative"
    >
      {Array.from({ length: 24 }).map((_, i) => (
        <motion.span
          key={i}
          className="absolute left-1/2 top-1/2 w-1.5 h-1.5 rounded-full"
          style={{
            background: i % 2 === 0 ? '#B08080' : '#FFFBFE',
            boxShadow: '0 0 8px rgba(176,128,128,0.8)',
          }}
          initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
          animate={{
            x: Math.cos((i / 24) * Math.PI * 2) * 220,
            y: Math.sin((i / 24) * Math.PI * 2) * 220,
            opacity: 0,
            scale: 0,
          }}
          transition={{ duration: 1.2, ease: 'easeOut', delay: 0.05 }}
        />
      ))}

      <motion.div
        className="inline-flex w-20 h-20 rounded-full bg-primary-500/20 border-2 border-primary-400 items-center justify-center mb-6 relative"
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: 0.1, duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
        style={{ boxShadow: '0 0 60px rgba(176,128,128,0.6)' }}
      >
        <Check className="w-10 h-10 text-primary-200" strokeWidth={3} />
      </motion.div>

      <motion.h1
        className="text-4xl md:text-5xl font-bold text-white mb-3"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.6 }}
      >
        Welcome, founder.
      </motion.h1>
      <motion.p
        className="text-gray-300 text-base mb-8"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.5 }}
      >
        Receipt sent to <span className="text-white">{email}</span>. Setting up your dashboard…
      </motion.p>

      <motion.div
        className="inline-flex items-center gap-2 text-primary-300 text-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8, duration: 0.4 }}
      >
        <motion.div
          className="w-2 h-2 rounded-full bg-primary-400"
          animate={{ scale: [1, 1.4, 1], opacity: [1, 0.5, 1] }}
          transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
        />
        Creating your account
      </motion.div>
    </motion.div>
  );
}

